import React, {useContext, useEffect, useState} from "react";
import BasicDataModule from "./basic_data_module";
import API from "../api/api";
import {Context} from "../utils/store";
import Preferences from "../utils/preferences";

const NamedQueryModule = ({
                              queryName,
                              source = "redshift",
                              formatters = {},
                              onCloseClick = {},
                              onDataChanged = {},
                              isDisabled = false
}) => {
    const api = API(source)
    const prefs = Preferences()
    const [feed, setFeed] = useState([])
    const [statusMsg, setStatusMsg] = useState("")
    const [status, setStatus] = useState("empty")
    const [state, dispatch] = useContext(Context);
    const [queryMeta, setQueryMeta] = useState({});
    const [querySql, setQuerySql] = useState({});
    const [localFilters, setLocalFilters] = useState({});
    const [publicLinks, setPublicLinks] = useState([]);
    const [publicLinksVisible, setPublicLinksVisible] = useState(false);
    let isShuttingDown = false

    const mergeFilters = () => {
        //console.log("localFilters", localFilters)
        var fs = Object.assign({}, state.filters, localFilters);
        for(var f in fs){
            //console.log("merging:", f, fs[f])
            fs[f] = JSON.stringify(fs[f])
        }
        return fs
    }

    let delayTimer
    const delay = (fn, delayMilliSecs) => {
        if(delayTimer) {
            clearTimeout(delayTimer)
        }
        delayTimer = setTimeout(fn, delayMilliSecs)
        setStatusMsg("loading ...")
        setStatus("loading")
    }

    const loadData = async() => {
        const filters_merged = mergeFilters()
        //console.log("loading data ", queryName, filters_merged)
        if(isShuttingDown || isDisabled) {
            setStatusMsg("disabled")
            return;
        }
        setStatusMsg("loading...")
        var data = await api.runNamedQuery(queryName, filters_merged)


        setFeed(data)
        if(data.msg){
            setStatusMsg(data.msg)
            setStatus("OK")
        } else if(data.error) {
            setStatusMsg(data.error)
            setStatus("error")
        } else {
            setStatusMsg("OK")
            setStatus("OK")
        }
        if (typeof onDataChanged === 'function') onDataChanged(data)
    }

    const loadMeta = async() => {
        //console.log("loading meta ", queryName)
        setStatusMsg("loading query info")
        setStatus("loading")
        var meta = await api.getNamedQueryMeta(queryName)
        if(isShuttingDown) return;

        const metaLessGlobals = {}
        for(var name in meta){
            if( !(name in state.filters)) {
                metaLessGlobals[name] = meta[name]
            }
        }

        const defaults = getDefaultFilterValues(metaLessGlobals)
        setLocalFilters(defaults)
        for(let f in metaLessGlobals){
            if(defaults[f]) metaLessGlobals[f].default = defaults[f]
        }
        setQueryMeta(metaLessGlobals)

    }

    const loadSql = async() => {
        const sql = await api.getNamedQuerySql(queryName)
        if(isShuttingDown || isDisabled) return;

        setQuerySql(sql)
    }

    const loadPublicLinks = async() => {
        const map = await api.getMyPublicAccessTokens(queryName)
        if(isShuttingDown || isDisabled) return;

        console.log("loadPublicLinks", map)
        const links = Object.entries(map).map( entry => {
            let token,v
            [token, v] = entry
            return {
                text: v.path + " " + v.datetime,
                url: api.getQueryPublicUrl(token, "csv", v.path, mergeFilters())
            }
        } )
        console.log(links)
        setPublicLinks(links)
    }

    const getDefaultForFilter = (queryName, name, f) => {
        let value = prefs.get(queryName + '-' + name) // get the default from the preferences
        console.log("getDefaultValueForFilter: ", queryName, name, value, f.type)
        switch(f.type){
            case 'list':
                if(value instanceof Array) break
                value = f.default || f.value || f.options || []
                break
            case 'select':
                if(typeof value === 'string' && value) break
                value = f.default || f.value || (f.options && f.options[0]) || ''
                break
            case 'select-multiple':
                if(value instanceof Array && value.length > 0) break
                value = f.default || f.value || f.options || ''
                break
            case 'date':
                if(typeof value === 'string') break
                value = f.default || f.value || new Date().toISOString().slice(0,10)
                break
            default:
                if(typeof value === 'string' || !isNaN(value)) break
                value = f.default || f.value || ''
        }
        return value;
    }

    const getDefaultFilterValues = (meta) => {
        const fs = {}
        for(let filterName in meta) {
            let value = getDefaultForFilter(queryName, filterName, meta[filterName])
            fs[filterName] = value
        }
        return fs
    }

    const onFilterChange = (change) => {
        console.log("onFilterChange", change)
        const newFilters = localFilters
        newFilters[change.name] = change.value
        setLocalFilters(newFilters)
        prefs.put(queryName + "-" + change.name, change.value)
        delay(loadData, 2000)
    }

    const onCopyLinkClick = async (e) => {
        if(!publicLinks.length) {
            await api.allowPublicAccessToQuery(queryName, mergeFilters())
            if(isShuttingDown || isDisabled) return;
            await loadPublicLinks()
        }
    }

    const cleanup = () => {
        isShuttingDown = true
        if(delayTimer) {
            clearTimeout(delayTimer)
        }
    }

    useEffect( () => {
        loadMeta().then((p)=>setStatus("Meta OK"), setStatus)
        loadSql().then((p)=>setStatus("SQL OK"), setStatus)
        loadPublicLinks().then((p)=>setStatus("Links OK"), setStatus)
        return cleanup
    }, [])

    useEffect( () => {
        if(localFilters) delay(loadData, 2000)
        return cleanup
    }, [state.filters, localFilters, queryMeta])

    return <BasicDataModule
        className={`${queryName} ${status} ${isDisabled ? "disabled" : ""}`}
        feed={feed}
        formatters={formatters}
        statusMsg={statusMsg}
        title={queryName.replaceAll('_',' ')}
        publicLinks={publicLinks}
        onRequestRefresh={loadData}
        onFilterChange={onFilterChange}
        onCloseClick={onCloseClick}
        onCopyLinkClick={onCopyLinkClick}
        filters={queryMeta}
        sql={querySql}
    />
}

export default NamedQueryModule

// http://localhost/api/public/123/csv/orders?date_from=%222021-07-01%22&date_to=%222021-08-31%22&date_aggregation=%22month%22&error=%22%22
// http://localhost/api/public/123/csv/orders?date_from=%222021-07-01%22&date_to=%222021-08-31%22&date_aggregation=%22month%22&error=%22%22
// http://localhost/api/public/123/csv/accounts?date_from=%222021-07-01%22&date_to=%222021-08-31%22&date_aggregation=%22month%22&error=%22%22